import { useEffect, useState } from "react";
import { wfAPI } from "./wfApi";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Check, Sparkles, User, Briefcase, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FormField, errorRing } from "@/components/form/FormField";
import { useFormValidation } from "@/hooks/useFormValidation";
import { required, email as emailV, phone as phoneV, minLength } from "@/lib/validators";

// WF-2 · 3-step Employee Creation Wizard
// ─────────────────────────────────────────────────────────────
// Step 1  Employee Details
// Step 2  Employment
// Step 3  System Access (opt-in — checkbox default OFF)

const STEPS = [
  { key: "details",    label: "Details",         icon: User },
  { key: "employment", label: "Employment",      icon: Briefcase },
  { key: "access",     label: "System Access",   icon: ShieldCheck },
];

export default function CreateEmployeeDialog({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState({
    name: "", email: "", phone: "", preferred_language: "en", government_id: "",
    photo_url: "",
  });
  const [emp, setEmp] = useState({
    org_unit_id: "none", job_title_id: "none", position: "", date_joined: "",
    employment_type: "full_time",
  });
  const [access, setAccess] = useState({
    has_login: false, password: "",
  });
  const v = useFormValidation({
    name: [required("Name is required")],
    email: [required("Email is required"), emailV()],
    phone: [phoneV()],
    password: [minLength(8, "Password must be at least 8 characters")],
  });

  // Reference data
  const [orgUnits, setOrgUnits]         = useState([]);
  const [jobTitles, setJobTitles]       = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [ous, jts] = await Promise.all([
          wfAPI.orgUnits(),
          wfAPI.jobTitles(),
        ]);
        setOrgUnits(ous); setJobTitles(jts);
      } catch { /* silent */ }
    })();
  }, []);

  const canNext = () => {
    if (step === 0) return detail.name.trim().length > 0 && /.+@.+\..+/.test(detail.email);
    // Department + Role are REQUIRED per the simplified workflow.
    if (step === 1) return emp.org_unit_id && emp.org_unit_id !== "none"
                        && emp.job_title_id && emp.job_title_id !== "none";
    if (step === 2) {
      if (!access.has_login) return true;
      return access.password.length >= 8;
    }
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: detail.name.trim(), email: detail.email.trim().toLowerCase(),
        phone: detail.phone || null,
        preferred_language: detail.preferred_language,
        government_id: detail.government_id || null,
        photo_url: detail.photo_url || null,
        has_login: access.has_login,
        // Simplified workflow: department + role are captured at creation
        // so System Access shows them without a second re-assign step.
        department_id: emp.org_unit_id && emp.org_unit_id !== "none" ? emp.org_unit_id : null,
        job_title_id:  emp.job_title_id && emp.job_title_id !== "none" ? emp.job_title_id : null,
      };
      if (access.has_login) {
        payload.password = access.password;
      }
      const created = await wfAPI.createEmployee(payload);
      // Employment (Step 2) — only if a department was picked
      if (emp.org_unit_id && emp.org_unit_id !== "none") {
        try {
          await wfAPI.createEmployment(created.id, {
            org_unit_id: emp.org_unit_id,
            job_title_id: emp.job_title_id && emp.job_title_id !== "none" ? emp.job_title_id : null,
            position: emp.position || null,
            date_joined: emp.date_joined || null,
          });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Employee created — employment failed");
        }
      }
      toast.success(access.has_login
        ? `Employee & login created — status ‘Pending Activation’`
        : "Employee created — no login (can be granted later)");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create employee");
    } finally { setSaving(false); }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="employee-wizard">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-hocp-navy text-hocp-gold flex items-center justify-center"><StepIcon className="w-5 h-5"/></div>
            <div>
              <div className="font-display text-[16px] font-semibold text-hocp-navy">
                {STEPS[step].label}
              </div>
              <div className="text-[11.5px] text-slate-500">Step {step + 1} of {STEPS.length}</div>
            </div>
          </div>
          <button data-testid="wizard-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5"/></button>
        </div>
        {/* Progress */}
        <div className="px-6 py-3 flex items-center gap-2 bg-slate-50/50 border-b border-slate-100">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`flex items-center gap-1 text-[11px] uppercase tracking-wider ${i === step ? "text-hocp-navy font-semibold" : i < step ? "text-emerald-600" : "text-slate-400"}`}>
              {i < step ? <Check className="w-3 h-3"/> : null}
              <span>{s.label}</span>
              {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 mx-1 text-slate-300"/>}
            </div>
          ))}
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-details">
              <FormField label="Name" htmlFor="wf-emp-name" required error={v.fieldError("name")} labelClassName="text-[11px] uppercase tracking-wider text-slate-500">
                <Input id="wf-emp-name" data-testid="wf-emp-name" value={detail.name} onChange={(e) => setDetail({...detail, name: e.target.value})} onBlur={() => v.handleBlur("name", detail.name)} className={errorRing(v.fieldError("name"))} placeholder="Full name" autoFocus/>
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Email" htmlFor="wf-emp-email" required error={v.fieldError("email")} labelClassName="text-[11px] uppercase tracking-wider text-slate-500">
                  <Input id="wf-emp-email" data-testid="wf-emp-email" type="email" value={detail.email} onChange={(e) => setDetail({...detail, email: e.target.value})} onBlur={() => v.handleBlur("email", detail.email)} className={errorRing(v.fieldError("email"))} placeholder="name@hotel.com"/>
                </FormField>
                <FormField label="Phone" htmlFor="wf-emp-phone" optional error={v.fieldError("phone")} labelClassName="text-[11px] uppercase tracking-wider text-slate-500">
                  <Input id="wf-emp-phone" data-testid="wf-emp-phone" value={detail.phone} onChange={(e) => setDetail({...detail, phone: e.target.value})} onBlur={() => v.handleBlur("phone", detail.phone)} className={errorRing(v.fieldError("phone"))} placeholder="+91 …"/>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Preferred Language</label>
                  <Select value={detail.preferred_language} onValueChange={(v) => setDetail({...detail, preferred_language: v})}>
                    <SelectTrigger data-testid="wf-emp-lang"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="hi">Hindi</SelectItem>
                      <SelectItem value="bn">Bengali</SelectItem>
                      <SelectItem value="ta">Tamil</SelectItem>
                      <SelectItem value="te">Telugu</SelectItem>
                      <SelectItem value="mr">Marathi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Government ID</label>
                  <Input value={detail.government_id} onChange={(e) => setDetail({...detail, government_id: e.target.value})} placeholder="Aadhaar / PAN"/>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-employment">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Department</label>
                  <Select value={emp.org_unit_id} onValueChange={(v) => setEmp({...emp, org_unit_id: v})}>
                    <SelectTrigger data-testid="wf-emp-dept"><SelectValue placeholder="No department"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No department</SelectItem>
                      {orgUnits.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Job Title</label>
                  <Select value={emp.job_title_id} onValueChange={(v) => setEmp({...emp, job_title_id: v})}>
                    <SelectTrigger data-testid="wf-emp-jt"><SelectValue placeholder="Optional"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {jobTitles.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Employment Type</label>
                  <Select value={emp.employment_type} onValueChange={(v) => setEmp({...emp, employment_type: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full time</SelectItem>
                      <SelectItem value="part_time">Part time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Joining Date</label>
                  <DatePicker value={(emp.date_joined || '').slice(0,10)} onChange={(_v) => setEmp({...emp, date_joined: _v})}/>
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Position (free text, e.g. “Night Shift Lead”)</label>
                <Input value={emp.position} onChange={(e) => setEmp({...emp, position: e.target.value})}/>
              </div>
              <div className="text-[12px] text-slate-500 bg-slate-50 rounded-md p-3">
                Employment is optional at creation time — you can assign it later from Employees.
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-access">
              <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" data-testid="wf-emp-has-login"
                  className="mt-1 w-4 h-4 accent-hocp-navy"
                  checked={access.has_login}
                  onChange={(e) => setAccess({...access, has_login: e.target.checked})}/>
                <div className="flex-1">
                  <div className="text-[13.5px] font-semibold text-hocp-navy">This employee requires access to HOCP</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">
                    Leave unchecked for records like Housekeeping attendants or Cooks who don&rsquo;t use the system directly. You can grant access later from <span className="font-medium">Workforce → System Access</span>.
                  </div>
                </div>
              </label>

              {access.has_login && (
                <div className="space-y-4 pl-2 border-l-2 border-hocp-gold/60">
                  <FormField label="Temporary Password" htmlFor="wf-emp-pwd" required error={v.fieldError("password")} labelClassName="text-[11px] uppercase tracking-wider text-slate-500" hint="The employee will be forced to change this on first login.">
                    <Input id="wf-emp-pwd" type="text" data-testid="wf-emp-pwd" placeholder="Min. 8 chars"
                      value={access.password}
                      onBlur={() => v.handleBlur("password", access.password)}
                      className={errorRing(v.fieldError("password"))}
                      onChange={(e) => setAccess({...access, password: e.target.value})}/>
                  </FormField>

                  <div className="p-3 rounded-md bg-sky-50/60 border border-sky-200 text-[12.5px] text-sky-900" data-testid="wf-emp-access-note">
                    <div className="font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5"/> Access is granted separately</div>
                    <div className="text-slate-600 mt-1">
                      After creating this employee, open their profile and grant module-level access from the <span className="font-medium">Access</span> tab. No presets — every permission is toggled visually.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <button
            data-testid="wizard-back"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="h-9 px-4 text-[13px] text-slate-500 hover:text-hocp-navy disabled:opacity-40 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4"/> Back
          </button>
          <div className="flex items-center gap-2">
            {step < STEPS.length - 1 ? (
              <button data-testid="wizard-next"
                onClick={() => {
                  if (step === 0) {
                    const ok = v.validateAll({ name: detail.name, email: detail.email, phone: detail.phone, password: access.password });
                    if (!ok) return;
                    setStep(1);
                    return;
                  }
                  if (canNext()) setStep(step + 1);
                  else toast.error("Please select a department and job title");
                }}
                className="h-9 px-4 rounded-md bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-1">
                Next <ArrowRight className="w-4 h-4"/>
              </button>
            ) : (
              <button data-testid="wizard-finish"
                onClick={() => {
                  if (access.has_login && !(access.password.length >= 8)) {
                    v.handleBlur("password", access.password);
                    toast.error("Password must be at least 8 characters");
                    return;
                  }
                  submit();
                }} disabled={saving}
                className="h-9 px-4 rounded-md bg-hocp-mint text-hocp-deepTeal text-[13px] font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                <Check className="w-4 h-4"/> {saving ? "Creating…" : "Create Employee"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
